"use client"

import React from 'react'
import { Play, Video } from 'lucide-react'
import { Photo } from '../actions'

interface VideoThumbnailProps {
    photo: Photo
    onClick?: () => void
    className?: string
    showDuration?: boolean
    showVideoIcon?: boolean
}

/**
 * Format duration in seconds to MM:SS or HH:MM:SS format
 */
function formatDuration(seconds: number | null | undefined): string {
    if (!seconds || seconds <= 0) return ''
    
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    
    if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * VideoThumbnail component for displaying video thumbnails with play button overlay
 * 
 * Shows the video thumbnail image with a play button overlay and optional duration badge
 */
export default function VideoThumbnail({
    photo,
    onClick,
    className = '',
    showDuration = true,
    showVideoIcon = true,
}: VideoThumbnailProps) {
    const isVideo = photo.mediaType === 'video'
    
    // Debug logging
    if (!photo.thumbnailUrl) {
        console.error('VideoThumbnail: Missing thumbnailUrl for photo:', photo.id, photo)
    }
    
    if (!isVideo) {
        // Fallback for non-video photos (shouldn't happen, but handle gracefully)
        return (
            <div className={`relative inline-block ${className}`}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={photo.thumbnailUrl || photo.imageUrl}
                    alt={photo.description || `Photo ${photo.id}`}
                    className="object-cover rounded block"
                    style={{ height: '200px', width: 'auto', display: 'block' }}
                />
            </div>
        )
    }

    // If no thumbnail URL, show a placeholder
    if (!photo.thumbnailUrl) {
        return (
            <div
                className={`relative group cursor-pointer inline-block ${className}`}
                onClick={onClick}
                style={{ width: '200px', height: '200px' }}
            >
                <div className="relative w-full h-full overflow-hidden rounded border bg-black flex items-center justify-center">
                    <div className="text-white text-center p-4">
                        <Video className="w-8 h-8 mx-auto mb-2" />
                        <p className="text-xs">No thumbnail</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div
            className={`relative group cursor-pointer inline-block ${className}`}
            onClick={onClick}
            style={{ width: '200px', height: '200px' }}
        >
            {/* Video Thumbnail */}
            <div className="relative w-full h-full overflow-hidden rounded border bg-black">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                    src={photo.thumbnailUrl}
                    alt={photo.description || `Video ${photo.id}`}
                    className="object-cover w-full h-full"
                    style={{ width: '200px', height: '200px', objectFit: 'cover' }}
                    onError={(e) => {
                        console.error('VideoThumbnail: Failed to load thumbnail:', photo.thumbnailUrl, e)
                    }}
                />
                
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 group-hover:bg-opacity-40 transition-opacity pointer-events-none">
                    <div className="relative">
                        <div className="absolute inset-0 bg-white rounded-full opacity-20 blur-md"></div>
                        <Play className="relative w-12 h-12 text-white fill-white drop-shadow-lg" />
                    </div>
                </div>
                
                {/* Duration Badge (bottom right) */}
                {showDuration && photo.duration && (
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded flex items-center gap-1 pointer-events-none">
                        <Video className="w-3 h-3" />
                        <span className="font-medium">{formatDuration(photo.duration)}</span>
                    </div>
                )}
                
                {/* Video Icon Indicator (top left) */}
                {showVideoIcon && (
                    <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white p-1.5 rounded pointer-events-none">
                        <Video className="w-4 h-4" />
                    </div>
                )}
            </div>
        </div>
    )
}

